import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { bubbleImageSrc, cargoMediaSrc } from '@/lib/bubbleImage';
import { Package, PaperPlaneTilt, NotePencil, Microphone, Camera, Sparkle, Plus } from '@phosphor-icons/react';
import CargoComposer from '@/components/CargoComposer';
import DriftCompose from '@/components/DriftCompose';

const KIND_ICON = { wish: NotePencil, voice: Microphone, photo: Camera };

const CargoHub = () => {
  const [bubbles, setBubbles] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBubble, setActiveBubble] = useState(null);
  const [driftFor, setDriftFor] = useState(null);

  const load = async () => {
    try {
      const [mine, drifts] = await Promise.all([
        api.get('/bubbles/mine'),
        api.get('/drifts/mine'),
      ]);
      setBubbles(mine.data.bubbles || []);
      setOutgoing(drifts.data.drifts || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activeCargoFor = (b) =>
    (b.cargo || []).filter((c) => !c.consumed);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-bx-cyan">layer 3 · what they're carrying</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Cargo</h1>
        <p className="text-white/60 mt-2 text-sm max-w-xl">
          Every Bubble can carry one moment of yours. Wins. Wishes. Voice notes. Photos.
          When they meet someone, they show it. When they come home, they bring something back.
        </p>
      </div>

      {loading ? (
        <div className="text-white/60">listening…</div>
      ) : bubbles.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <p className="text-white/65">Breathe a Bubble into existence first.</p>
          <Link to="/breathe" className="inline-block mt-5 btn-volt px-6 py-3 rounded-full font-bold text-sm">Breathe one</Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bubbles.map((b) => {
              const cargo = activeCargoFor(b);
              const src = bubbleImageSrc(b);
              return (
                <div key={b.id} data-testid={`cargo-bubble-${b.id}`}
                  className="glass rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-full overflow-hidden bg-white/5">
                      {src ? <img src={src} alt={b.name} className="h-full w-full object-cover breathe" /> : null}
                    </div>
                    <div className="flex-1">
                      <Link to={`/bubble/${b.id}`} className="font-display font-bold hover:text-bx-cyan">{b.name}</Link>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">
                        {b.drifting ? 'drifting' : `${cargo.length} carrying`}
                      </div>
                    </div>
                  </div>

                  {cargo.length === 0 ? (
                    <div className="text-sm text-white/65">Hands are empty.</div>
                  ) : (
                    <div className="space-y-2">
                      {cargo.map((c) => {
                        const Icon = KIND_ICON[c.kind] || Sparkle;
                        return (
                          <div key={c.id} className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60 mb-1">
                              <Icon size={12} weight="duotone" className="text-bx-cyan" />
                              {c.kind}
                              {c.moment && <span className="text-bx-volt">· {c.moment}</span>}
                            </div>
                            {c.kind === 'wish' && c.text && (
                              <div className="text-sm italic">"{c.text}"</div>
                            )}
                            {c.kind === 'voice' && cargoMediaSrc(c) && (
                              <audio controls src={cargoMediaSrc(c)} className="w-full mt-1" />
                            )}
                            {c.kind === 'photo' && cargoMediaSrc(c) && (
                              <img alt="" src={cargoMediaSrc(c)} className="rounded-lg max-h-32 w-full object-cover mt-1" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex gap-2 mt-4">
                    {!b.drifting && (
                      <button
                        data-testid={`cargo-add-${b.id}`}
                        onClick={() => setActiveBubble(b)}
                        className="flex-1 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5">
                        <Plus size={12} weight="bold" /> add moment
                      </button>
                    )}
                    {cargo.length > 0 && !b.drifting && (
                      <button
                        data-testid={`cargo-drift-${b.id}`}
                        onClick={() => setDriftFor(b)}
                        className="flex-1 py-2 rounded-full btn-volt text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5">
                        <PaperPlaneTilt size={12} weight="bold" /> drift
                      </button>
                    )}
                    {b.drifting && (
                      <span className="flex-1 text-center py-2 text-xs text-bx-magenta animate-pulse">in transit…</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Outgoing drift status */}
          {outgoing.length > 0 && (
            <section className="mt-10">
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">recent drifts you sent</div>
              <div className="space-y-2">
                {outgoing.slice(0, 8).map((d) => (
                  <div key={d.id} data-testid={`outgoing-drift-${d.id}`}
                    className="glass rounded-xl px-4 py-3 flex items-center gap-3 text-sm">
                    <Package size={14} weight="duotone" className="text-bx-cyan" />
                    <span className="font-bold">{d.bubble_name}</span>
                    <span className="text-white/60">→</span>
                    <span className="text-white/70">{d.destination_label}</span>
                    <span className={`ml-auto text-[10px] uppercase tracking-widest font-bold ${
                      d.status === 'returned' ? 'text-bx-volt'
                        : d.status === 'landed' || d.status === 'opened' ? 'text-bx-cyan'
                        : 'text-bx-magenta animate-pulse'
                    }`}>
                      {d.status}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {activeBubble && (
        <CargoComposer
          bubble={activeBubble}
          open={true}
          onClose={() => setActiveBubble(null)}
          onAttached={load}
        />
      )}
      {driftFor && (
        <DriftCompose
          bubble={driftFor}
          open={true}
          onClose={() => setDriftFor(null)}
          onSent={load}
        />
      )}
    </div>
  );
};

export default CargoHub;
